import {
  formatSuffix,
  formatNameWithSuffix,
  getShortDisplayName,
  isUnnamedFirstName,
} from "./name.jsx";
import { isDashboardStartOrTopOfLineMember } from "./dashboardStartOfLineMember";

function trimNamePart(value) {
  if (value == null || value === "") return "";
  return String(value).trim();
}

function readLoggedInTopOfLineRoot() {
  try {
    const raw = localStorage.getItem("loggedIn_user_role");
    if (!raw) return null;
    const role = JSON.parse(raw);
    const root = role?.top_of_the_line ?? role?.topOfTheLine;
    return root && typeof root === "object" ? root : null;
  } catch {
    return null;
  }
}

/** Married surname for gen-1 display, e.g. Patterson + Witherspoon → Patterson-Witherspoon */
function formatStartTopMarriedSurname(maiden, marriedLast) {
  const m = trimNamePart(maiden);
  const l = trimNamePart(marriedLast);
  if (m && l && m.toLowerCase() !== l.toLowerCase()) {
    return `${m}-${l}`;
  }
  return l || m;
}

function resolveStartTopMaidenFromRoot(member) {
  const root = readLoggedInTopOfLineRoot();
  if (!root || !member) return "";

  const memberFirst = trimNamePart(member.first_name ?? member.firstName).toLowerCase();
  if (!memberFirst) return "";

  const rootFirst = trimNamePart(root.first_name ?? root.firstName).toLowerCase();
  const rootSpouseFirst = trimNamePart(
    root.spouse_first_name ?? root.spouseFirstName
  ).toLowerCase();

  if (memberFirst === rootSpouseFirst) {
    const maidenFromRoot = trimNamePart(
      root.spouse_maiden_name ?? root.spouseMaidenName ?? ""
    );
    if (maidenFromRoot) return maidenFromRoot;

    const spouseLastOnRoot = trimNamePart(
      root.spouse_last_name ?? root.spouseLastName ?? ""
    );
    const memberLast = trimNamePart(member.last_name ?? member.lastName);
    if (
      spouseLastOnRoot &&
      memberLast &&
      spouseLastOnRoot.toLowerCase() !== memberLast.toLowerCase()
    ) {
      return spouseLastOnRoot;
    }
    return "";
  }
  if (memberFirst === rootFirst) {
    return trimNamePart(root.maiden_name ?? root.maidenName ?? "");
  }
  return "";
}

function resolveStartTopMaidenName(member) {
  const fromMember = trimNamePart(member.maiden_name ?? member.maidenName);
  if (fromMember) return fromMember;
  return resolveStartTopMaidenFromRoot(member);
}

function resolveStartTopSuffix(member) {
  const suffixRaw = member?.suffix;
  if (typeof suffixRaw === "object" && suffixRaw !== null) {
    return suffixRaw.name || "";
  }
  return suffixRaw || "";
}

/**
 * Full name for Start/Top of Line (gen 1) on dashboard lists.
 * With maiden: "First Middle (Maiden) Last, Suffix" (e.g. Frances Virginia (Patterson) Witherspoon-Marrs).
 */
export function formatDashboardStartTopLineFullName(member) {
  if (!member || typeof member !== "object") return "";

  const first = trimNamePart(member.first_name ?? member.firstName);
  const middle = trimNamePart(member.middle_name ?? member.middleName);
  const special = trimNamePart(member.special_name);
  const middlePart = middle || special;
  const marriedLast = trimNamePart(member.last_name ?? member.lastName);
  const maiden = resolveStartTopMaidenName(member);
  const suffix = resolveStartTopSuffix(member);

  if (maiden && (first || marriedLast)) {
    const beforeMaiden = [first, middlePart]
      .filter((part) => part != null && String(part).trim() !== "")
      .join(" ");
    let base = beforeMaiden ? `${beforeMaiden} (${maiden})` : `(${maiden})`;
    if (marriedLast) base = `${base} ${marriedLast}`.trim();
    if (suffix) {
      const formattedSuffix = formatSuffix(suffix);
      return base ? `${base}, ${formattedSuffix}` : formattedSuffix;
    }
    return base;
  }

  const surname = formatStartTopMarriedSurname(maiden, marriedLast);
  const built = formatNameWithSuffix(first, middlePart, surname, suffix);
  if (built) return built;

  const combined = [member.full_name, member.fullName, member.name].find(
    (v) => v != null && String(v).trim() !== ""
  );
  if (combined) return String(combined).trim();

  return formatDashboardMemberDisplayName(member);
}

export function isDashboardInLawMember(member) {
  if (!member || typeof member !== "object") return false;
  return (
    member.is_in_laws === true ||
    member.is_in_laws === "true" ||
    member.is_in_laws === 1
  );
}

function getDashboardMemberMaritalStatus(member) {
  if (!member || typeof member !== "object") return null;
  const raw = member.marital_status;
  if (raw == null || raw === "") return null;
  const parsed = typeof raw === "string" ? parseInt(raw, 10) : Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function isDashboardMarriedMember(member) {
  return getDashboardMemberMaritalStatus(member) === 2;
}

function hasDashboardWeddingAnniversaryDate(member) {
  if (!member || typeof member !== "object") return false;
  const raw = member.wedding_anniversary ?? member.weddingAnniversary;
  return raw != null && raw !== "";
}

/** Dashboard APIs may return anniversaries as an array or an id-keyed object. */
export function normalizeDashboardAnniversariesList(anniversaries) {
  if (!anniversaries) return [];
  if (Array.isArray(anniversaries)) return anniversaries;
  if (typeof anniversaries === "object") {
    return Object.entries(anniversaries).map(([key, item]) => ({
      ...(item && typeof item === "object" ? item : {}),
      id: item?.id ?? key,
    }));
  }
  return [];
}

export function filterDashboardMarriedAnniversaries(anniversaries) {
  const list = normalizeDashboardAnniversariesList(anniversaries);
  return list.filter((member) => {
    if (isDashboardMarriedMember(member)) return true;
    // Upcoming dashboard entries are often id-keyed maps without marital_status.
    if (
      getDashboardMemberMaritalStatus(member) == null &&
      hasDashboardWeddingAnniversaryDate(member)
    ) {
      return true;
    }
    return false;
  });
}

export function isDashboardMarriedInLawMember(member) {
  return (
    isDashboardInLawMember(member) &&
    isDashboardMarriedMember(member)
  );
}

export function isDashboardInRelationshipInLawMember(member) {
  return (
    isDashboardInLawMember(member) &&
    getDashboardMemberMaritalStatus(member) === 3 &&
    !isDashboardStartOrTopOfLineMember(member)
  );
}

export function isDashboardLivingMember(member) {
  if (!member || typeof member !== "object") return false;
  return Number(member.present_or_deceased) === 1;
}

export function isDashboardLivingInLawMember(member) {
  return (
    isDashboardMarriedInLawMember(member) &&
    isDashboardLivingMember(member) &&
    !isDashboardStartOrTopOfLineMember(member)
  );
}

export function isDashboardDeceasedMember(member) {
  if (!member || typeof member !== "object") return false;
  return Number(member.present_or_deceased) === 2;
}

function hasDeathDateValue(value) {
  if (value == null) return false;
  const raw = String(value).trim();
  if (!raw || raw === "0" || raw.toLowerCase() === "null") return false;
  return true;
}

/**
 * Deceased check for the dashboard Upcoming lists. Those payloads do not always
 * carry `present_or_deceased` (the In Memoriam rows in particular only ship a date
 * of passing), so a recorded death date marks the member as deceased too.
 */
export function isDashboardUpcomingDeceasedMember(member) {
  if (!member || typeof member !== "object") return false;
  if (isDashboardDeceasedMember(member)) return true;
  if (Number(member.present_or_deceased) === 1) return false;
  return hasDeathDateValue(
    member.date_of_death ??
      member.dateOfDeath ??
      member.date_of_passing ??
      member.dateOfPassing ??
      member.death_date ??
      member.dod
  );
}

/** Anniversary rows name a couple: either side being deceased marks the row. */
export function isDashboardUpcomingDeceasedCouple(anniversary) {
  if (!anniversary || typeof anniversary !== "object") return false;
  if (isDashboardUpcomingDeceasedMember(anniversary)) return true;

  const spouseStatus = Number(
    anniversary.spouse_present_or_deceased ?? anniversary.spousePresentOrDeceased
  );
  if (spouseStatus === 2) return true;
  if (spouseStatus === 1) return false;

  return hasDeathDateValue(
    anniversary.spouse_date_of_death ??
      anniversary.spouseDateOfDeath ??
      anniversary.spouse_date_of_passing ??
      anniversary.spouse_dod
  );
}

const leadingWordKey = (value) =>
  trimNamePart(value).split(/\s+/)[0].toLowerCase();

const suffixKey = (value) => resolveSuffixValue(value).replace(/\.$/, "").toLowerCase();

/**
 * In-law rows name their descendant spouse in parentheses — "Mayumi Smith (Terry)".
 * The row ships only that spouse's name, no id or alive/deceased flag, so the
 * spouse is looked up on the roster by name (and line colour) instead.
 * Returns true when that spouse is deceased too, false otherwise (unknown included).
 */
export function isDashboardDescendantSpouseDeceased(member, roster = []) {
  if (!member || typeof member !== "object") return false;

  const ownStatus = Number(
    member.descendants_present_or_deceased ?? member.descendant_present_or_deceased
  );
  if (ownStatus === 2) return true;
  if (ownStatus === 1) return false;
  if (hasDeathDateValue(member.descendants_date_of_death ?? member.descendant_date_of_death)) {
    return true;
  }

  const candidates = findDashboardDescendantSpouseOnRoster(member, roster);
  return candidates.length > 0 && candidates.every(isDashboardUpcomingDeceasedMember);
}

/**
 * The roster rows that read as an in-law's descendant spouse — matched on the
 * spouse's name the in-law row ships, and on line colour when both carry one.
 */
function findDashboardDescendantSpouseOnRoster(member, roster = []) {
  const firstKey = leadingWordKey(
    member.descendants_first_name ?? member.descendant_first_name
  );
  if (!firstKey || !Array.isArray(roster)) return [];
  const rowSuffix = suffixKey(member.descendants_suffix ?? member.descendant_suffix);
  const rowLast = trimNamePart(
    member.descendants_last_name ?? member.descendant_last_name
  ).toLowerCase();
  const rowColor = trimNamePart(member.family_line_hex_code || member.hex_code).toLowerCase();

  let candidates = roster.filter((person) => {
    if (!person || isDashboardInLawMember(person)) return false;
    if (person.id != null && member.id != null && String(person.id) === String(member.id)) {
      return false;
    }
    if (leadingWordKey(person.first_name) !== firstKey) return false;
    if (suffixKey(person.suffix) !== rowSuffix) return false;
    const last = trimNamePart(person.last_name).toLowerCase();
    if (rowLast && last && last !== rowLast) return false;
    const color = trimNamePart(person.family_line_hex_code || person.hex_code).toLowerCase();
    if (rowColor && color && color !== rowColor) return false;
    return true;
  });

  // Same name twice on a line: keep the one married to this in-law, when the
  // roster names spouses.
  if (candidates.length > 1) {
    const inLawFirst = leadingWordKey(member.first_name);
    const married = candidates.filter(
      (person) => leadingWordKey(person.spouse_first_name) === inLawFirst
    );
    if (married.length) candidates = married;
  }

  return candidates;
}

/**
 * A row's family line — `{ colorName, hexCode }`. Some in-law records (deceased
 * ones especially, e.g. Mayumi Smith on the Red Line) come back with no line
 * colour of their own, which left them out of every per-line count; they take
 * their descendant spouse's line instead.
 */
export function getDashboardMemberLineColor(member, roster = []) {
  const own = {
    colorName: trimNamePart(member?.color_name || member?.family_line_color),
    hexCode: trimNamePart(member?.hex_code || member?.family_line_hex_code),
  };
  if (own.colorName || !isDashboardInLawMember(member)) return own;

  const spouse = findDashboardDescendantSpouseOnRoster(member, roster).find(
    (person) => trimNamePart(person?.color_name || person?.family_line_color)
  );
  if (!spouse) return own;
  return {
    colorName: trimNamePart(spouse.color_name || spouse.family_line_color),
    hexCode: trimNamePart(spouse.hex_code || spouse.family_line_hex_code) || own.hexCode,
  };
}

export function isDashboardDescendantMember(member) {
  if (!member || typeof member !== "object") return false;
  return (
    !isDashboardInLawMember(member) && !isDashboardStartOrTopOfLineMember(member)
  );
}

export function isDashboardDeceasedDescendantMember(member) {
  return isDashboardDescendantMember(member) && isDashboardDeceasedMember(member);
}

export function isDashboardLivingDescendantMember(member) {
  return isDashboardDescendantMember(member) && isDashboardLivingMember(member);
}

/**
 * "Related": the blood family plus the couple at the head of each line. Same
 * list as Descendants with Start/Top of Line folded back in, so the only rows
 * left out are the in-laws who married in below the head.
 */
export function isDashboardRelatedMember(member) {
  if (!member || typeof member !== "object") return false;
  return (
    isDashboardStartOrTopOfLineMember(member) || !isDashboardInLawMember(member)
  );
}

export function isDashboardLivingRelatedMember(member) {
  return isDashboardRelatedMember(member) && isDashboardLivingMember(member);
}

export function isDashboardDeceasedRelatedMember(member) {
  return isDashboardRelatedMember(member) && isDashboardDeceasedMember(member);
}

/**
 * Every deceased in-law is listed, whatever marital status their record was left
 * on — Married, Widowed, Separated or In Relationship alike (e.g. Mayumi Smith
 * under Terry Smith on the Red Line).
 */
export function isDashboardDeceasedInLawMember(member) {
  return (
    isDashboardInLawMember(member) &&
    isDashboardDeceasedMember(member) &&
    !isDashboardStartOrTopOfLineMember(member)
  );
}

function trimmedLastName(member) {
  const v = member?.last_name ?? member?.lastName;
  if (v == null || v === "") return "";
  return String(v).trim();
}

function resolveSuffixValue(raw) {
  if (raw == null || raw === "") return "";
  if (typeof raw === "object" && raw !== null) {
    return trimNamePart(raw.name ?? raw.suffix ?? raw.value ?? "");
  }
  return trimNamePart(raw);
}

function resolveMemberSuffix(member) {
  return resolveSuffixValue(member?.suffix);
}

function buildPersonDisplayName(first, last, suffix, specialName) {
  const firstPart = trimNamePart(first);
  if (!firstPart) return "";

  const lastPart = trimNamePart(last);

  if (suffix) {
    const built = formatNameWithSuffix(firstPart, null, lastPart || null, suffix);
    if (built) return built;
  }

  if (specialName) {
    const built = formatNameWithSuffix(firstPart, specialName, lastPart || null, null);
    if (built) return built;
    return trimNamePart(specialName);
  }

  const built = formatNameWithSuffix(firstPart, null, lastPart || null, null);
  return built || firstPart;
}

/**
 * Family Line/Color column on member rows: short head name (first + suffix/special, no last).
 */
export function formatDashboardFamilyLineHeadName(row) {
  if (!row || typeof row !== "object") return "";

  let firstName = trimNamePart(
    row.family_line_first_name ??
      row.head_first_name ??
      row.hol_first_name ??
      row.head_of_line_first_name ??
      row.familyLineFirstName ??
      row.headFirstName
  );

  const lastName = trimNamePart(
    row.family_line_last_name ??
      row.head_last_name ??
      row.hol_last_name ??
      row.head_of_line_last_name ??
      row.familyLineLastName ??
      row.headLastName
  );

  const suffix = resolveSuffixValue(
    row.family_line_suffix ??
      row.head_suffix ??
      row.hol_suffix ??
      row.head_of_line_suffix ??
      row.familyLineSuffix ??
      row.headSuffix
  );

  const specialName = trimNamePart(
    row.family_line_special_name ??
      row.head_special_name ??
      row.hol_special_name ??
      row.head_of_line_special_name ??
      row.familyLineSpecialName ??
      row.headSpecialName
  );

  // Upcoming APIs sometimes stuff "First Last" into the first-name field.
  if (firstName && lastName) {
    const parts = firstName.split(/\s+/).filter(Boolean);
    if (
      parts.length >= 2 &&
      parts[parts.length - 1].toLowerCase() === lastName.toLowerCase()
    ) {
      firstName = parts.slice(0, -1).join(" ");
    }
  }

  if (!firstName) return "";

  if (isUnnamedFirstName(firstName) && lastName) {
    return buildPersonDisplayName(firstName, lastName, suffix, specialName);
  }

  return getShortDisplayName(firstName, suffix, specialName);
}

/**
 * Heads of Line/Color page: first + last + suffix/special when fields exist.
 */
export function formatHeadOfLineMemberDisplayName(member) {
  if (!member || typeof member !== "object") return "";

  const first = trimNamePart(member.first_name ?? member.firstName);
  const last = trimmedLastName(member);
  const suffix = resolveMemberSuffix(member);
  const special = trimNamePart(member.special_name ?? member.specialName);

  return buildPersonDisplayName(first, last, suffix, special);
}

/**
 * Heads of Line/Color page rows: the head's full name, i.e. the standard head name
 * with the middle name kept in ("Frank Marion Witherspoon, Sr."). Falls back to the
 * shared head format when the record carries no middle name.
 */
export function formatHeadOfLineFullDisplayName(member) {
  if (!member || typeof member !== "object") return "";

  const middle = trimNamePart(member.middle_name ?? member.middleName);
  const first = trimNamePart(member.first_name ?? member.firstName);
  if (!middle || !first) return formatHeadOfLineMemberDisplayName(member);

  const last = trimmedLastName(member);
  const suffix = resolveMemberSuffix(member);

  return (
    formatNameWithSuffix(first, middle, last, suffix) ||
    formatHeadOfLineMemberDisplayName(member)
  );
}

function hasStructuredMemberNameFields(member) {
  return (
    Boolean(trimNamePart(member?.first_name ?? member?.firstName)) ||
    Boolean(trimmedLastName(member)) ||
    Boolean(resolveMemberSuffix(member)) ||
    Boolean(trimNamePart(member?.special_name ?? member?.specialName)) ||
    (isDashboardInLawMember(member) && Boolean(trimmedLastName(member)))
  );
}

/**
 * Recently Added widget: first + last + suffix when applicable (e.g. "Frank Witherspoon, Sr.").
 * Falls back to API `name` when structured fields are missing.
 */
export function formatRecentlyAddedMemberDisplayName(member) {
  if (!member || typeof member !== "object") return "";

  if (isDashboardStartOrTopOfLineMember(member)) {
    const startTop = formatDashboardStartTopLineFullName(member);
    if (startTop) return startTop;
  }

  if (hasStructuredMemberNameFields(member)) {
    const first = trimNamePart(member.first_name ?? member.firstName);
    const last = trimmedLastName(member);
    const suffix = resolveMemberSuffix(member);
    const special = trimNamePart(member.special_name ?? member.specialName);

    let displayName = "";

    if (suffix) {
      displayName = formatNameWithSuffix(first, null, last, suffix);
    } else if (special) {
      displayName = formatNameWithSuffix(first, special, last, null) || special;
    } else {
      displayName = formatNameWithSuffix(first, null, last, null);
    }

    if (displayName) {
      return shouldAppendDashboardDescendantSpouse(member)
        ? appendDashboardDescendantSpouseName(displayName, member)
        : displayName;
    }
  }

  const fallback = String(
    member?.name ?? member?.full_name ?? member?.fullName ?? ""
  ).trim();
  if (fallback) {
    return shouldAppendDashboardDescendantSpouse(member)
      ? appendDashboardDescendantSpouseName(fallback, member)
      : fallback;
  }

  return formatDashboardMemberDisplayName(member);
}

/** Member payloads code gender as 1 = male, 2 = female. */
function isDashboardMaleMember(member) {
  const raw =
    member?.gender ??
    member?.Gender ??
    member?.member_gender ??
    member?.memberGender;
  if (raw == null || String(raw).trim() === "") return false;
  return String(raw).trim() === "1";
}

/**
 * In Memoriam rows: a woman is remembered under her first and maiden names
 * ("Frances Patterson"), not the married full name the other dashboard lists
 * show ("Frances Virginia (Patterson) Witherspoon-Marrs"). Men, and women with
 * no maiden name on record, keep the standard dashboard name.
 *
 * The Upcoming payloads frequently ship no `gender`, so a maiden name on record
 * is the working female signal: the member form only allows that field for
 * gender 2, and an explicit gender 1 still opts the row out.
 */
export function formatDashboardMemorialMemberDisplayName(member) {
  if (!member || typeof member !== "object") return "";

  if (!isDashboardMaleMember(member)) {
    const first = trimNamePart(member.first_name ?? member.firstName);
    const maiden = resolveStartTopMaidenName(member);
    if (first && maiden) {
      const suffix = resolveMemberSuffix(member);
      const displayName =
        formatNameWithSuffix(first, null, maiden, suffix) || `${first} ${maiden}`;
      // In-law rows still carry their "(descendant)" tag; only the surname changes.
      return shouldAppendDashboardDescendantSpouse(member)
        ? appendDashboardDescendantSpouseName(displayName, member)
        : displayName;
    }
  }

  return formatRecentlyAddedMemberDisplayName(member);
}

function trimmedSpouseLastName(row) {
  const v = row?.spouse_last_name ?? row?.spouseLastName;
  if (v == null || v === "") return "";
  return String(v).trim();
}

function isSpouseInLawRow(row) {
  return (
    row?.spouse_is_in_laws === true ||
    row?.spouse_is_in_laws === "true" ||
    row?.spouse_is_in_laws === 1
  );
}

/**
 * Partner name for in-relationship rows: first + suffix or special initial. The
 * partner of a descendant is an in-law, so they keep their last name too.
 */
export function formatDashboardInRelationshipPartnerShortName(member) {
  const descendantName = formatDashboardDescendantSpouseShortName(member, "primary");
  if (descendantName) return descendantName;

  const firstName = trimNamePart(
    member?.spouse_first_name ?? member?.spouseFirstName
  );
  const suffix = resolveSuffixValue(
    member?.spouse_suffix ?? member?.spouseSuffix
  );
  const specialName = trimNamePart(
    member?.spouse_special_name ?? member?.spouseSpecialName
  );
  if (isDashboardInLawMember(member)) {
    return getShortDisplayName(firstName, suffix, specialName);
  }
  return buildPersonDisplayName(
    firstName,
    trimmedSpouseLastName(member),
    suffix,
    specialName
  );
}

export function getDashboardInRelationshipMemberNameParts(member) {
  const { baseName } = getDashboardMemberNameParts(member, {
    appendDescendantSpouse: false,
  });
  const partnerName = formatDashboardInRelationshipPartnerShortName(member);
  return { baseName, partnerName };
}

/** Short descendant spouse name for in-law parentheses: first + suffix or special initial. */
function formatDashboardDescendantSpouseShortName(member, side = "primary") {
  if (side === "spouse") {
    const firstName = trimNamePart(
      member?.spouse_descendants_first_name ??
        member?.spouseDescendantsFirstName ??
        member?.spouse_descendant_first_name ??
        member?.spouseDescendantFirstName
    );
    const suffix = resolveSuffixValue(
      member?.spouse_descendants_suffix ??
        member?.spouseDescendantsSuffix ??
        member?.spouse_descendant_suffix ??
        member?.spouseDescendantSuffix
    );
    const specialName = trimNamePart(
      member?.spouse_descendants_special_name ??
        member?.spouseDescendantsSpecialName ??
        member?.spouse_descendant_special_name ??
        member?.spouseDescendantSpecialName
    );
    return getShortDisplayName(firstName, suffix, specialName);
  }

  const firstName = trimNamePart(
    member?.descendants_first_name ??
      member?.descendantsFirstName ??
      member?.descendant_first_name ??
      member?.descendantFirstName
  );
  const suffix = resolveSuffixValue(
    member?.descendants_suffix ??
      member?.descendantsSuffix ??
      member?.descendant_suffix ??
      member?.descendantSuffix
  );
  const specialName = trimNamePart(
    member?.descendants_special_name ??
      member?.descendantsSpecialName ??
      member?.descendant_special_name ??
      member?.descendantSpecialName
  );
  return getShortDisplayName(firstName, suffix, specialName);
}

function isDashboardSpouseInLawRow(row) {
  return isSpouseInLawRow(row);
}

/** Couple rows: true when the spouse side of the row is the in-law. */
export function isDashboardSpouseInLawMember(row) {
  return isSpouseInLawRow(row);
}

function shouldAppendDashboardDescendantSpouse(member, options = {}) {
  const { side = "primary", strictHeadForInLaw = false } = options;

  if (isDashboardStartOrTopOfLineMember(member)) return false;

  if (side === "spouse") {
    if (!isDashboardSpouseInLawRow(member)) return false;
    return Boolean(formatDashboardDescendantSpouseShortName(member, side));
  }

  const inLaw =
    isDashboardInLawMember(member) &&
    (!strictHeadForInLaw || Boolean(String(member?.head_first_name || "").trim()));

  return inLaw;
}

/**
 * Living and deceased in-law rows: append descendant spouse short name in parentheses.
 * e.g. "Sherry Gassaway (Wesley)", "Lisa Witherspoon (Frank, Sr.)", "Mayumi Smith (Mary M)"
 */
export function appendDashboardDescendantSpouseName(displayName, member, side = "primary") {
  const descendantShortName = formatDashboardDescendantSpouseShortName(member, side);
  if (!displayName || !descendantShortName) return displayName;
  return `${displayName} (${descendantShortName})`;
}

export function getDashboardMemberNameParts(member, options = {}) {
  const side = options.side || "primary";
  const descendantSpouseName =
    options.appendDescendantSpouse !== false &&
    shouldAppendDashboardDescendantSpouse(member, options)
      ? formatDashboardDescendantSpouseShortName(member, side)
      : "";

  const baseName = formatDashboardMemberDisplayName(member, {
    ...options,
    appendDescendantSpouse: false,
  });

  return { baseName, descendantSpouseName };
}

export function getRecentlyAddedMemberNameParts(member) {
  const descendantSpouseName = shouldAppendDashboardDescendantSpouse(member)
    ? formatDashboardDescendantSpouseShortName(member)
    : "";
  const baseName = formatRecentlyAddedMemberDisplayName(member);
  if (!descendantSpouseName) {
    return { baseName, descendantSpouseName: "" };
  }
  const suffix = ` (${descendantSpouseName})`;
  if (baseName.endsWith(suffix)) {
    return {
      baseName: baseName.slice(0, -suffix.length),
      descendantSpouseName,
    };
  }
  return { baseName, descendantSpouseName: "" };
}

export function getAnniversaryPersonNameParts(
  anniversary,
  { firstName, suffix, specialName, side, hasSpouseSide = true }
) {
  const primaryLast = trimmedLastName(anniversary);
  const spouseLast = trimmedSpouseLastName(anniversary);
  // The Start/Top of Line couple's records carry their middle names in the
  // first-name field ("Joseph Alexander", "Frances Virginia") and Frances's
  // later married surname ("Witherspoon-Marrs"). The couple reads as on the
  // other dashboard lists: "Joseph & Frances Witherspoon, Sr.".
  const isStartTopCouple = isDashboardStartOrTopOfLineMember(anniversary);
  if (isStartTopCouple) {
    firstName = trimNamePart(firstName).split(/\s+/)[0];
  }
  const spouseLastToShow = isStartTopCouple
    ? primaryLast || spouseLast
    : spouseLast || primaryLast;

  if (side === "primary") {
    // The first person shows first name only: the married surname is carried by the
    // spouse side ("Paul & Linda Hawkins") and the suffix / special initial moves to
    // the end of the couple string (see getAnniversaryCoupleTrailingName).
    // A row with no spouse side keeps its own last name so the surname is not lost.
    // An in-law or unnamed first person keeps their own last name whenever it
    // differs from the couple surname ("Mary Jones & Paul Hawkins").
    const includeLast =
      Boolean(primaryLast) &&
      (!hasSpouseSide ||
        (shouldShowDashboardLastName(anniversary, firstName) &&
          primaryLast.toLowerCase() !== spouseLastToShow.toLowerCase()));
    const baseName = formatAnniversaryPersonDisplayName(
      firstName,
      "",
      "",
      includeLast ? primaryLast : ""
    );
    const descendantSpouseName =
      !isDashboardStartOrTopOfLineMember(anniversary) &&
      shouldAppendDashboardDescendantSpouse(anniversary, {
        side: "primary",
      })
        ? formatDashboardDescendantSpouseShortName(anniversary, "primary")
        : "";
    return { baseName, descendantSpouseName };
  }

  const baseName = formatAnniversaryPersonDisplayName(
    firstName,
    suffix,
    specialName,
    spouseLastToShow || ""
  );
  const descendantSpouseName =
    !isDashboardStartOrTopOfLineMember(anniversary) &&
    shouldAppendDashboardDescendantSpouse(anniversary, {
      side: "spouse",
    })
      ? formatDashboardDescendantSpouseShortName(anniversary, "spouse")
      : "";
  return { baseName, descendantSpouseName };
}

/** In-laws and unnamed members always show their last name on dashboard reports. */
export function shouldShowDashboardLastName(member, firstName) {
  return (
    isDashboardInLawMember(member) ||
    isUnnamedFirstName(firstName ?? member?.first_name ?? member?.firstName)
  );
}

/** First + last + suffix/special, e.g. "Mayumi Tanaka", "Paul Hawkins, Jr.". */
export function formatDashboardFullPersonName(first, last, suffix, specialName) {
  return buildPersonDisplayName(first, last, resolveSuffixValue(suffix), trimNamePart(specialName));
}

/**
 * Dashboard rows: first + optional last (in-laws) + suffix/special at the end.
 * e.g. "Frank Patterson, Sr.", "Roger, Sr.", "Mary M Patterson"
 *
 * @param {object} member
 * @param {{ unnamedLabel?: string, strictHeadForInLaw?: boolean, appendDescendantSpouse?: boolean }} [options]
 *   strictHeadForInLaw — when true, only append the descendant spouse parenthetical if `head_first_name` is set
 *   (matches Birthdays / Memorials asterisk logic).
 *   appendDescendantSpouse — when not false, append `(descendants short name)` for in-laws.
 */
export function formatDashboardMemberDisplayName(member, options = {}) {
  const unnamedLabel = Object.prototype.hasOwnProperty.call(options, "unnamedLabel")
    ? options.unnamedLabel
    : "Unnamed";
  const strictHeadForInLaw = Boolean(options.strictHeadForInLaw);
  const appendDescendantSpouse = options.appendDescendantSpouse !== false;

  const firstTrim = String(member?.first_name ?? member?.firstName ?? "").trim();
  const firstPart = firstTrim || unnamedLabel;
  const suffix = resolveMemberSuffix(member);
  const specialName = trimNamePart(member?.special_name ?? member?.specialName);

  const last = trimmedLastName(member);
  // In-laws and unnamed members ("UnNamed Boy") always keep their last name on
  // every report, so the row says whose they are. strictHeadForInLaw only
  // gates the "(descendant)" parenthetical below, never the last name.
  const includeLast = Boolean(last) && shouldShowDashboardLastName(member, firstPart);

  let displayName = buildPersonDisplayName(
    firstPart,
    includeLast ? last : "",
    suffix,
    specialName
  );

  if (
    appendDescendantSpouse &&
    shouldAppendDashboardDescendantSpouse(member, { strictHeadForInLaw })
  ) {
    displayName = appendDashboardDescendantSpouseName(displayName, member);
  }

  return displayName;
}

const AMPERSAND_SPLIT_RE = /\s*&\s*/;

function normalizeAmpersandSpacing(text) {
  if (text == null || typeof text !== "string") return "";
  return text
    .split(AMPERSAND_SPLIT_RE)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .join(" & ");
}

function formatAnniversaryPersonDisplayName(firstName, suffix, specialName, lastName) {
  const result = buildPersonDisplayName(
    firstName,
    lastName || "",
    suffix,
    specialName
  );
  return normalizeAmpersandSpacing(result);
}

/**
 * Suffix / special initial for an anniversary couple row. It belongs at the very end
 * of the couple string ("Paul & Linda Hawkins, Sr.", "Mary & Bobby Brown M"), not
 * after the first person's name. Returns the rendered text including its separator.
 */
export function getAnniversaryCoupleTrailingName(suffix, specialName) {
  const suffixValue = resolveSuffixValue(suffix);
  if (suffixValue) return `, ${formatSuffix(suffixValue)}`;

  const special = trimNamePart(specialName);
  if (special) return ` ${special}`;

  return "";
}

/**
 * One side of a couple row on the anniversaries dashboard (primary or spouse).
 * The married surname is shown once on the spouse side; the first person shows a
 * last name only when there is no spouse side to carry it.
 */
export function formatAnniversaryPersonSideDisplayName(
  anniversary,
  { firstName, suffix, specialName, side, hasSpouseSide = true }
) {
  const { baseName, descendantSpouseName } = getAnniversaryPersonNameParts(anniversary, {
    firstName,
    suffix,
    specialName,
    side,
    hasSpouseSide,
  });
  return descendantSpouseName
    ? appendDashboardDescendantSpouseName(baseName, anniversary, side)
    : baseName;
}

/** Split a single person's name string into name words and an optional trailing suffix. */
function splitCouplePersonName(part) {
  const [namePortion, ...suffixPortions] = String(part).split(",");
  return {
    words: namePortion.trim().split(/\s+/).filter(Boolean),
    suffix: suffixPortions.join(",").trim(),
  };
}

/**
 * Collapse a couple string that repeats a shared married last name.
 * e.g. "Paul Hawkins & Linda Hawkins" -> "Paul & Linda Hawkins"
 *      "Paul Hawkins, Sr. & Linda Hawkins" -> "Paul & Linda Hawkins, Sr."
 * The original string is returned unchanged when the last names differ, a
 * person has no last name, or the two sides carry conflicting suffixes.
 */
export function collapseSharedCoupleLastName(coupleName) {
  const value = trimNamePart(coupleName);
  if (!value) return "";

  const parts = value
    .split(AMPERSAND_SPLIT_RE)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length !== 2) return value;

  const [primary, spouse] = parts.map(splitCouplePersonName);
  if (primary.words.length < 2 || spouse.words.length < 2) return value;

  const primaryLast = primary.words[primary.words.length - 1];
  const spouseLast = spouse.words[spouse.words.length - 1];
  if (primaryLast.toLowerCase() !== spouseLast.toLowerCase()) return value;

  const suffixes = [primary.suffix, spouse.suffix].filter(Boolean);
  const distinctSuffixes = [...new Set(suffixes.map((s) => s.toLowerCase()))];
  if (distinctSuffixes.length > 1) return value;

  const primaryFirst = primary.words.slice(0, -1).join(" ");
  const spouseFirst = spouse.words.slice(0, -1).join(" ");

  let collapsed = `${primaryFirst} & ${spouseFirst} ${primaryLast}`
    .replace(/\s+/g, " ")
    .trim();
  if (suffixes[0]) collapsed = `${collapsed}, ${suffixes[0]}`;
  return collapsed;
}

function resolveUpcomingAnniversaryCoupleFields(anniversary) {
  let rootFirst = trimNamePart(
    anniversary?.root_user_first_name ??
      anniversary?.rootUserFirstName ??
      anniversary?.member_first_name ??
      anniversary?.memberFirstName ??
      anniversary?.first_name ??
      anniversary?.firstName
  );
  let spouseFirst = trimNamePart(
    anniversary?.spouse_first_name ?? anniversary?.spouseFirstName
  );
  const spouseLast = trimNamePart(
    anniversary?.spouse_last_name ?? anniversary?.spouseLastName
  );
  const rootSuffix = resolveSuffixValue(
    anniversary?.root_user_suffix ??
      anniversary?.rootUserSuffix ??
      anniversary?.member_suffix ??
      anniversary?.memberSuffix ??
      anniversary?.suffix
  );
  const rootSpecial = trimNamePart(
    anniversary?.root_user_special_name ??
      anniversary?.rootUserSpecialName ??
      anniversary?.member_special_name ??
      anniversary?.memberSpecialName ??
      anniversary?.special_name ??
      anniversary?.specialName
  );

  if (rootFirst.includes("&")) {
    const parts = rootFirst
      .split(AMPERSAND_SPLIT_RE)
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length >= 2) {
      rootFirst = parts[0];
      if (!spouseFirst) {
        spouseFirst = parts.slice(1).join(" & ");
      }
    }
  } else if (!rootFirst && spouseFirst.includes("&")) {
    const parts = spouseFirst
      .split(AMPERSAND_SPLIT_RE)
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length >= 2) {
      rootFirst = parts[0];
      spouseFirst = parts.slice(1).join(" & ");
    }
  }

  if (!rootFirst) {
    const coupleName = trimNamePart(
      anniversary?.couple_name ?? anniversary?.coupleName ?? anniversary?.name
    );
    if (coupleName.includes("&")) {
      const parts = coupleName
        .split(AMPERSAND_SPLIT_RE)
        .map((p) => p.trim())
        .filter(Boolean);
      if (parts.length >= 2) {
        const primary = splitCouplePersonName(parts[0]);
        if (primary.words.length > 0) {
          rootFirst = primary.words[0];
        }
      }
    }
  }

  return {
    rootFirst: normalizeAmpersandSpacing(rootFirst),
    rootSuffix,
    rootSpecial,
    spouseFirst: normalizeAmpersandSpacing(spouseFirst),
    spouseLast,
  };
}

/**
 * Name shown for the dashboard Upcoming "Wedding Anniversaries" list:
 * root first & spouse first + spouse last, with suffix/special initial at the end
 * (e.g. "Paul & Linda Hawkins, Sr.", "Mary & Bobby Brown M").
 */
export function formatDashboardUpcomingCoupleName(anniversary) {
  if (!anniversary || typeof anniversary !== "object") return "";

  const {
    rootFirst,
    rootSuffix,
    rootSpecial,
    spouseFirst,
    spouseLast,
  } = resolveUpcomingAnniversaryCoupleFields(anniversary);

  const spouseName = [spouseFirst, spouseLast].filter(Boolean).join(" ");

  // An in-law or unnamed root keeps their own last name when the couple
  // surname does not already say it ("Mary Jones & Paul Hawkins").
  const rootLast = trimNamePart(
    anniversary.root_user_last_name ??
      anniversary.rootUserLastName ??
      anniversary.last_name ??
      anniversary.lastName
  );
  const rootName =
    rootLast &&
    shouldShowDashboardLastName(anniversary, rootFirst) &&
    (!spouseName || rootLast.toLowerCase() !== spouseLast.toLowerCase())
      ? `${rootFirst} ${rootLast}`
      : rootFirst;

  let coupleName = "";
  if (rootFirst && spouseName) {
    coupleName = `${rootName} & ${spouseName}`;
  } else if (rootFirst) {
    coupleName = rootName;
  } else {
    const fallback = collapseSharedCoupleLastName(
      anniversary.couple_name ?? anniversary.coupleName ?? anniversary.name
    );
    if (fallback) return fallback;
    return spouseName;
  }

  // Suffix / special initial belong at the end of the full couple string
  // ("Paul & Linda Hawkins, Sr."), not after the root first name.
  if (rootSuffix) {
    return `${coupleName}, ${formatSuffix(rootSuffix)}`;
  }
  if (rootSpecial) {
    return `${coupleName} ${rootSpecial}`;
  }
  return coupleName;
}
